import { z } from 'zod';

/**
 * User Roles Enum
 */
export enum UserRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  OPERATOR = 'OPERATOR',
  VIEWER = 'VIEWER'
}

/**
 * Permission Scopes
 */
export enum PermissionScope {
  PRODUCTS = 'products',
  INVENTORY = 'inventory',
  WAREHOUSES = 'warehouses',
  USERS = 'users',
  REPORTS = 'reports',
  SETTINGS = 'settings'
}

/**
 * Permission Actions
 */
export enum PermissionAction {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  MANAGE = 'manage'
}

/**
 * Permission Definition
 */
export interface Permission {
  scope: PermissionScope;
  actions: PermissionAction[];
}

/**
 * User Authentication Methods
 */
export enum AuthMethod {
  PASSWORD = 'password',
  PIN = 'pin',
  BIOMETRIC = 'biometric'
}

/**
 * User Schema
 */
export const userSchema = z.object({
  id: z.string().uuid(),
  username: z.string().min(3).max(50),
  email: z.string().email(),
  passwordHash: z.string(),
  pin: z.string().optional(),
  role: z.nativeEnum(UserRole),
  permissions: z.array(z.object({
    scope: z.nativeEnum(PermissionScope),
    actions: z.array(z.nativeEnum(PermissionAction))
  })),
  authMethods: z.array(z.nativeEnum(AuthMethod)),
  lastLogin: z.date().optional(),
  failedAttempts: z.number().min(0),
  locked: z.boolean(),
  active: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type User = z.infer<typeof userSchema>;

/**
 * Session Schema
 */
export const sessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  token: z.string(),
  refreshToken: z.string(),
  expiresAt: z.date(),
  lastActivity: z.date(),
  deviceInfo: z.object({
    id: z.string(),
    name: z.string(),
    platform: z.string(),
    browser: z.string().optional()
  }),
  active: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Session = z.infer<typeof sessionSchema>;

/**
 * Auth Token Payload
 */
export interface TokenPayload {
  userId: string;
  username: string;
  role: UserRole;
  permissions: Permission[];
  sessionId: string;
}

/**
 * Authentication Service Interface
 */
export interface IAuthService {
  // User Management
  createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User>;
  updateUser(id: string, userData: Partial<User>): Promise<User>;
  deleteUser(id: string): Promise<void>;
  getUserById(id: string): Promise<User | null>;
  getUserByUsername(username: string): Promise<User | null>;
  
  // Authentication
  login(username: string, password: string): Promise<Session>;
  loginWithPin(username: string, pin: string): Promise<Session>;
  loginWithBiometric(username: string, biometricToken: string): Promise<Session>;
  logout(sessionId: string): Promise<void>;
  refreshSession(refreshToken: string): Promise<Session>;
  
  // PIN Management
  setupPin(userId: string, pin: string): Promise<void>;
  changePin(userId: string, currentPin: string, newPin: string): Promise<void>;
  resetPin(userId: string): Promise<string>;
  
  // Biometric Management
  enableBiometric(userId: string): Promise<string>;
  disableBiometric(userId: string): Promise<void>;
  
  // Session Management
  getActiveSessions(userId: string): Promise<Session[]>;
  invalidateSession(sessionId: string): Promise<void>;
  invalidateAllSessions(userId: string): Promise<void>;
  
  // Security
  validatePassword(password: string): boolean;
  validatePin(pin: string): boolean;
  isSessionValid(sessionId: string): Promise<boolean>;
  hasPermission(userId: string, scope: PermissionScope, action: PermissionAction): Promise<boolean>;
}

/**
 * Auth Repository Interface
 */
export interface IAuthRepository {
  // User Operations
  createUser(user: User): Promise<User>;
  updateUser(user: User): Promise<User>;
  deleteUser(id: string): Promise<void>;
  getUserById(id: string): Promise<User | null>;
  getUserByUsername(username: string): Promise<User | null>;
  getUserByEmail(email: string): Promise<User | null>;

  // Session Operations
  createSession(session: Session): Promise<Session>;
  updateSession(session: Session): Promise<Session>;
  deleteSession(id: string): Promise<void>;
  getSessionById(id: string): Promise<Session | null>;
  getSessionsByUserId(userId: string): Promise<Session[]>;
  deleteSessionsByUserId(userId: string): Promise<void>;
}